import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import type { PaginatedResponse, WalletRecordItem } from './types';

export interface UseWalletRecordsArgs {
  walletId: string | number;
  limit?: number;
  offset?: number;
  dateFrom?: string;
  dateTo?: string;
  direction?: 'D' | 'C';
}

export interface UseWalletRecordsReturn {
  data: PaginatedResponse<WalletRecordItem> | undefined;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

const DEFAULT_LIMIT = 20;

export function useWalletRecords(args: UseWalletRecordsArgs): UseWalletRecordsReturn {
  const { walletId, limit = DEFAULT_LIMIT, offset = 0, dateFrom, dateTo, direction } = args;

  const params: Record<string, string> = {
    limit: String(limit),
    offset: String(offset),
  };
  if (dateFrom) params.dateFrom = dateFrom;
  if (dateTo) params.dateTo = dateTo;
  if (direction) params.direction = direction;

  const walletIdStr = String(walletId).trim();

  const query = useQuery<PaginatedResponse<WalletRecordItem>>({
    queryKey: ['wallet-records', walletIdStr, params],
    queryFn: async () => {
      return api.get<PaginatedResponse<WalletRecordItem>>(
        `/api/wallets/${encodeURIComponent(walletIdStr)}/records`,
        { params },
      );
    },
    enabled: walletIdStr.length > 0,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: () => {
      void query.refetch();
    },
  };
}
